import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Clock,
  Calendar as CalendarIcon,
  User,
  Plus,
  Edit2,
  Trash2,
  Check,
  X,
  Timer,
  TrendingUp,
  Zap,
} from "lucide-react";

// office_hub.css start line 5526 to 6142
export default function SimpleTimeTrackingSection() {
  const [timeEntries, setTimeEntries] = useState([
    {
      id: "1",
      assignee: "John Doe",
      assigneeEmoji: "👨‍💼",
      hours: 2,
      minutes: 30,
      workDate: "2024-12-05",
      description: "Initial design wireframes",
      createdAt: new Date("2024-12-05T10:00:00"),
    },
    {
      id: "2",
      assignee: "Jane Smith",
      assigneeEmoji: "👩‍💻",
      hours: 1,
      minutes: 45,
      workDate: "2024-12-06",
      description: "API research and planning",
      createdAt: new Date("2024-12-06T14:00:00"),
    },
    {
      id: "3",
      assignee: "John Doe",
      assigneeEmoji: "👨‍💼",
      hours: 2,
      minutes: 10,
      workDate: "2024-12-05",
      description: "High-fidelity mockups",
      createdAt: new Date("2024-12-05T15:00:00"),
    },
  ]);

  const [formData, setFormData] = useState({
    hours: "",
    minutes: "",
    workDate: "",
    description: "",
    assignee: "John Doe",
    assigneeEmoji: "👨‍💼",
  });

  const [editingId, setEditingId] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [hoveredEntry, setHoveredEntry] = useState(null);

  const assigneeOptions = [
    { name: "John Doe", emoji: "👨‍💼" },
    { name: "Jane Smith", emoji: "👩‍💻" },
    { name: "Mike Johnson", emoji: "👨‍🔧" },
    { name: "Sarah Williams", emoji: "👩‍🎨" },
    { name: "Danish Ali", emoji: "👨‍💻" },
  ];

  const calculateTotalTime = () => {
    const totalMinutes = timeEntries.reduce((sum, entry) => {
      return sum + entry.hours * 60 + entry.minutes;
    }, 0);

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return { hours, minutes };
  };

  const formatTime = (hours, minutes) => {
    const h = hours > 0 ? `${hours}h` : "";
    const m = minutes > 0 ? `${minutes}m` : "";
    return `${h} ${m}`.trim() || "0m";
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.hours && !formData.minutes) return;
    if (!formData.workDate || !formData.description) return;

    const hours = parseInt(formData.hours, 10) || 0;
    const minutes = parseInt(formData.minutes, 10) || 0;

    if (editingId) {
      setTimeEntries((prev) =>
        prev.map((entry) =>
          entry.id === editingId
            ? {
                ...entry,
                hours,
                minutes,
                workDate: formData.workDate,
                description: formData.description,
                assignee: formData.assignee,
                assigneeEmoji: formData.assigneeEmoji,
              }
            : entry,
        ),
      );
      setEditingId(null);
    } else {
      const newEntry = {
        id: Date.now().toString(),
        assignee: formData.assignee,
        assigneeEmoji: formData.assigneeEmoji,
        hours,
        minutes,
        workDate: formData.workDate,
        description: formData.description,
        createdAt: new Date(),
      };

      setTimeEntries((prev) => [newEntry, ...prev]);
    }

    setFormData({
      hours: "",
      minutes: "",
      workDate: "",
      description: "",
      assignee: "John Doe",
      assigneeEmoji: "👨‍💼",
    });

    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 2000);
  };

  const handleEdit = (entry) => {
    setFormData({
      hours: entry.hours.toString(),
      minutes: entry.minutes.toString(),
      workDate: entry.workDate,
      description: entry.description,
      assignee: entry.assignee,
      assigneeEmoji: entry.assigneeEmoji,
    });
    setEditingId(entry.id);

    // window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = (id) => {
    setTimeEntries((prev) => prev.filter((entry) => entry.id !== id));
  };

  const handleAssigneeChange = (assigneeName) => {
    const selected = assigneeOptions.find((a) => a.name === assigneeName);
    if (selected) {
      setFormData((prev) => ({
        ...prev,
        assignee: selected.name,
        assigneeEmoji: selected.emoji,
      }));
    }
  };

  const totalTime = useMemo(() => calculateTotalTime(), [timeEntries]);
  const uniqueContributors = useMemo(
    () => new Set(timeEntries.map((e) => e.assignee)).size,
    [timeEntries],
  );

  const stats = [
    {
      icon: <TrendingUp className="SimpleTimeTrackingSection_ClassV1_IconMd" />,
      label: "Total Entries",
      value: String(timeEntries.length),
      variant: "CyanBlue",
    },
    {
      icon: <Timer className="SimpleTimeTrackingSection_ClassV1_IconMd" />,
      label: "Total Time Logged",
      value: formatTime(totalTime.hours, totalTime.minutes),
      variant: "PurplePink",
    },
    {
      icon: <Zap className="SimpleTimeTrackingSection_ClassV1_IconMd" />,
      label: "Active Contributors",
      value: String(uniqueContributors),
      variant: "AmberOrange",
    },
  ];

  return (
    <section className="SimpleTimeTrackingSection_ClassV1_Section">
      {/* Animated Background Elements */}
      {/* <div className="SimpleTimeTrackingSection_ClassV1_BgLayer">
        <motion.div
          className="SimpleTimeTrackingSection_ClassV1_Blob SimpleTimeTrackingSection_ClassV1_Blob__Cyan"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="SimpleTimeTrackingSection_ClassV1_Blob SimpleTimeTrackingSection_ClassV1_Blob__Purple"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.3, 0.5] }}
          transition={{ duration: 8, repeat: Infinity, delay: 1 }}
        />
      </div> */}

      <div className="SimpleTimeTrackingSection_ClassV1_Container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="SimpleTimeTrackingSection_ClassV1_Header"
        >
          <div className="SimpleTimeTrackingSection_ClassV1_Pill">
            <Clock className="SimpleTimeTrackingSection_ClassV1_IconSm" />
            <span className="SimpleTimeTrackingSection_ClassV1_PillText">
              Simple Time Tracking
            </span>
          </div>

          <h2 className="SimpleTimeTrackingSection_ClassV1_Title">
            Simple Time Tracking
          </h2>

          <p className="SimpleTimeTrackingSection_ClassV1_SubTitle">
            Log time manually with detailed notes. Multiple assignees can track
            time on the same task.
          </p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="SimpleTimeTrackingSection_ClassV1_MainGrid">
          {/* Left: Add Time Entry Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="SimpleTimeTrackingSection_ClassV1_FormCard">
              <h3 className="SimpleTimeTrackingSection_ClassV1_FormTitle">
                {editingId ? (
                  <>
                    <Edit2 className="SimpleTimeTrackingSection_ClassV1_IconMd SimpleTimeTrackingSection_ClassV1_IconPurple" />
                    Edit Time Entry
                  </>
                ) : (
                  <>
                    <Plus className="SimpleTimeTrackingSection_ClassV1_IconMd SimpleTimeTrackingSection_ClassV1_IconBlue" />
                    Add Time Entry
                  </>
                )}
              </h3>

              <form
                onSubmit={handleSubmit}
                className="SimpleTimeTrackingSection_ClassV1_Form"
              >
                {/* Hours and Minutes */}
                <div className="SimpleTimeTrackingSection_ClassV1_Row2">
                  <div>
                    <label className="SimpleTimeTrackingSection_ClassV1_Label">
                      Hours
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="23"
                      value={formData.hours}
                      onChange={(e) =>
                        setFormData({ ...formData, hours: e.target.value })
                      }
                      placeholder="2"
                      className="SimpleTimeTrackingSection_ClassV1_Input"
                    />
                  </div>

                  <div>
                    <label className="SimpleTimeTrackingSection_ClassV1_Label">
                      Minutes
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={formData.minutes}
                      onChange={(e) =>
                        setFormData({ ...formData, minutes: e.target.value })
                      }
                      placeholder="30"
                      className="SimpleTimeTrackingSection_ClassV1_Input"
                    />
                  </div>
                </div>

                {/* Work Date */}
                <div>
                  <label className="SimpleTimeTrackingSection_ClassV1_Label">
                    Work Date
                  </label>
                  <div className="SimpleTimeTrackingSection_ClassV1_InputWrap">
                    <input
                      type="date"
                      value={formData.workDate}
                      onChange={(e) =>
                        setFormData({ ...formData, workDate: e.target.value })
                      }
                      required
                      className="SimpleTimeTrackingSection_ClassV1_Input"
                    />
                    {/* <CalendarIcon className="SimpleTimeTrackingSection_ClassV1_InputIcon" /> */}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="SimpleTimeTrackingSection_ClassV1_Label">
                    What did you work on?
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Describe your work..."
                    required
                    rows={4}
                    className="SimpleTimeTrackingSection_ClassV1_Textarea"
                  />
                </div>

                {/* Assignee */}
                <div>
                  <label className="SimpleTimeTrackingSection_ClassV1_Label">
                    Assignee
                  </label>
                  <div className="SimpleTimeTrackingSection_ClassV1_InputWrap">
                    <select
                      value={formData.assignee}
                      onChange={(e) => handleAssigneeChange(e.target.value)}
                      className="SimpleTimeTrackingSection_ClassV1_Select"
                    >
                      {assigneeOptions.map((option) => (
                        <option key={option.name} value={option.name}>
                          {option.emoji} {option.name}
                        </option>
                      ))}
                    </select>
                    <User className="SimpleTimeTrackingSection_ClassV1_InputIcon" />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="SimpleTimeTrackingSection_ClassV1_SubmitRow">
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="SimpleTimeTrackingSection_ClassV1_PrimaryBtn"
                  >
                    <motion.div
                      className="SimpleTimeTrackingSection_ClassV1_PrimaryOverlay"
                      initial={{ x: "100%" }}
                      whileHover={{ x: 0 }}
                      transition={{ duration: 0.3 }}
                    />
                    <span className="SimpleTimeTrackingSection_ClassV1_PrimaryContent">
                      {editingId ? (
                        <>
                          <Check className="SimpleTimeTrackingSection_ClassV1_IconSm" />
                          Update Time Log
                        </>
                      ) : (
                        <>
                          <Plus className="SimpleTimeTrackingSection_ClassV1_IconSm" />
                          Save Time Log
                        </>
                      )}
                    </span>
                  </motion.button>

                  {editingId && (
                    <motion.button
                      type="button"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      onClick={() => {
                        setEditingId(null);
                        setFormData({
                          hours: "",
                          minutes: "",
                          workDate: "",
                          description: "",
                          assignee: "John Doe",
                          assigneeEmoji: "👨‍💼",
                        });
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="SimpleTimeTrackingSection_ClassV1_CancelBtn"
                    >
                      <X className="SimpleTimeTrackingSection_ClassV1_IconSm" />
                      Cancel
                    </motion.button>
                  )}
                </div>
              </form>

              {/* Success Message */}
              <AnimatePresence>
                {showSuccess && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="SimpleTimeTrackingSection_ClassV1_SuccessMsg"
                  >
                    <Check className="SimpleTimeTrackingSection_ClassV1_IconSm" />
                    <span>Time entry saved successfully!</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Right: Time Entries List */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="SimpleTimeTrackingSection_ClassV1_ListCard">
              {/* Header */}
              <div className="SimpleTimeTrackingSection_ClassV1_ListHeader">
                <h3 className="SimpleTimeTrackingSection_ClassV1_ListTitle">
                  <Timer className="SimpleTimeTrackingSection_ClassV1_IconMd" />
                  Time Entries
                </h3>

                <motion.div
                  className="SimpleTimeTrackingSection_ClassV1_TotalBadge"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <span className="SimpleTimeTrackingSection_ClassV1_TotalText">
                    Total: {formatTime(totalTime.hours, totalTime.minutes)}
                  </span>
                </motion.div>
              </div>

              {/* Entries */}
              <div className="SimpleTimeTrackingSection_ClassV1_EntriesScroll">
                <AnimatePresence mode="popLayout">
                  {timeEntries.length === 0 ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="SimpleTimeTrackingSection_ClassV1_EmptyState"
                    >
                      <Clock className="SimpleTimeTrackingSection_ClassV1_EmptyIcon" />
                      <p>No time entries yet. Add your first one!</p>
                    </motion.div>
                  ) : (
                    timeEntries.map((entry, index) => (
                      <motion.div
                        key={entry.id}
                        layout
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -100, scale: 0.8 }}
                        transition={{ delay: index * 0.05 }}
                        onHoverStart={() => setHoveredEntry(entry.id)}
                        onHoverEnd={() => setHoveredEntry(null)}
                        className="SimpleTimeTrackingSection_ClassV1_EntryCard"
                      >
                        {/* Hover Glow */}
                        {hoveredEntry === entry.id && (
                          <motion.div
                            layoutId="SimpleTimeTrackingSection_ClassV1_hoverGlow"
                            className="SimpleTimeTrackingSection_ClassV1_EntryGlow"
                            transition={{
                              type: "spring",
                              stiffness: 300,
                              damping: 30,
                            }}
                          />
                        )}

                        <div className="SimpleTimeTrackingSection_ClassV1_EntryInner">
                          <div className="SimpleTimeTrackingSection_ClassV1_EntryLeft">
                            <motion.div
                              className="SimpleTimeTrackingSection_ClassV1_EntryAvatar"
                              whileHover={{
                                rotate: [0, -10, 10, -10, 0],
                                scale: 1.1,
                              }}
                              transition={{ duration: 0.5 }}
                            >
                              {entry.assigneeEmoji}
                            </motion.div>

                            <div className="SimpleTimeTrackingSection_ClassV1_EntryDetails">
                              <div className="SimpleTimeTrackingSection_ClassV1_EntryTopRow">
                                <div className="SimpleTimeTrackingSection_ClassV1_EntryTextBlock">
                                  <h4 className="SimpleTimeTrackingSection_ClassV1_EntryName">
                                    {entry.assignee}
                                  </h4>
                                  <p className="SimpleTimeTrackingSection_ClassV1_EntryDesc">
                                    {entry.description}
                                  </p>
                                </div>

                                <div className="SimpleTimeTrackingSection_ClassV1_EntryTime">
                                  {formatTime(entry.hours, entry.minutes)}
                                </div>
                              </div>

                              <div className="SimpleTimeTrackingSection_ClassV1_EntryDateRow">
                                <CalendarIcon className="SimpleTimeTrackingSection_ClassV1_Icon2xs" />
                                {new Date(entry.workDate).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  },
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="SimpleTimeTrackingSection_ClassV1_EntryActions">
                            <motion.button
                              type="button"
                              onClick={() => handleEdit(entry)}
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className="SimpleTimeTrackingSection_ClassV1_IconBtn"
                            >
                              <Edit2 className="SimpleTimeTrackingSection_ClassV1_IconSm" />
                            </motion.button>

                            <motion.button
                              type="button"
                              onClick={() => handleDelete(entry.id)}
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className="SimpleTimeTrackingSection_ClassV1_IconBtn SimpleTimeTrackingSection_ClassV1_IconBtn__Danger"
                            >
                              <Trash2 className="SimpleTimeTrackingSection_ClassV1_IconSm" />
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    ))
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="SimpleTimeTrackingSection_ClassV1_StatsGrid"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.05, y: -5 }}
              className="SimpleTimeTrackingSection_ClassV1_StatCard"
            >
              <div
                className={`SimpleTimeTrackingSection_ClassV1_StatIconBox SimpleTimeTrackingSection_ClassV1_StatIconBox__${stat.variant}`}
              >
                {stat.icon}
              </div>
              <div className="SimpleTimeTrackingSection_ClassV1_StatValue">
                {stat.value}
              </div>
              <div className="SimpleTimeTrackingSection_ClassV1_StatLabel">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
